import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'omniforge_cookie_consent';

type ConsentStatus = 'pending' | 'accepted' | 'declined';

export function CookieConsent() {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check localStorage for existing consent
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (storedConsent === 'accepted' || storedConsent === 'declined') {
      setConsentStatus(storedConsent as ConsentStatus);
    } else {
      // Delay showing the banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setConsentStatus('accepted');
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setConsentStatus('declined');
    setIsVisible(false);
  };

  // Don't render if user has already made a choice
  if (consentStatus !== 'pending') {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          role="dialog"
          aria-label="Cookie consent"
          aria-describedby="cookie-consent-description"
        >
          <div className="container-marketing">
            <div className="bg-white rounded-xl shadow-elevated border border-gray-200 p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Icon */}
                <div className="hidden md:flex w-10 h-10 rounded-lg bg-accent-light items-center justify-center flex-shrink-0">
                  <CookieIcon className="w-5 h-5 text-accent" />
                </div>

                {/* Text */}
                <div className="flex-1" id="cookie-consent-description">
                  <p className="text-sm md:text-base text-ink">
                    We use cookies to enhance your experience and analyze site traffic.{' '}
                    <Link to="/privacy" className="link-accent">
                      Learn more
                    </Link>
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 flex-shrink-0">
                  <button
                    onClick={handleDecline}
                    className="px-4 py-2 text-sm font-medium text-muted hover:text-ink transition-colors rounded-lg hover:bg-gray-100"
                  >
                    Decline
                  </button>
                  <button
                    onClick={handleAccept}
                    className="btn-primary px-5 py-2 text-sm"
                  >
                    Accept
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CookieIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="8" cy="9" r="1" fill="currentColor" />
      <circle cx="15" cy="8" r="1" fill="currentColor" />
      <circle cx="10" cy="14" r="1" fill="currentColor" />
      <circle cx="16" cy="14" r="1" fill="currentColor" />
      <circle cx="12" cy="11" r="1" fill="currentColor" />
    </svg>
  );
}
